import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { GlassPanel } from "../layout/GlassPanel/GlassPanel";
import { Stepper } from "./Stepper";
import type { Wizard } from "./useWizard";

/**
 * Un paso, visto desde el armazón: cómo se llama y qué se hace en él.
 *
 * Es a propósito lo MÍNIMO. Qué campos lleva, qué se valida al salir y
 * qué se pinta dentro son cosas de quien lo usa; el armazón solo necesita
 * el rótulo del progreso y el nombre con el que se anuncia la región.
 */
export interface FormWizardStep {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
}

export interface FormWizardProps {
  /** Los pasos, en orden. Su longitud es el total del indicador. */
  steps: readonly FormWizardStep[];
  /** El estado del recorrido (`useWizard`). */
  wizard: Wizard;
  /** Nombre de la región de progreso para lectores de pantalla. */
  progressLabel: string;
  /**
   * El foco al llegar a un paso que trae un error: pone el foco EN el
   * campo y devuelve `true`. Sin él —o si devuelve `false`— el foco va a
   * la región del paso, que se anuncia con su nombre.
   *
   * Lo normal es que salga de `useStepFields`.
   */
  focusStepError?: (step: number) => boolean;
  /**
   * Cuántos intentos de envío van (`formState.submitCount`). Al subir, el
   * armazón lleva al primer paso que tenga un error.
   */
  submitCount?: number;
  /** El primer paso con error, o `-1`. Ver `useStepFields`. */
  firstStepWithError?: () => number;
  /** El paso pintado. */
  children: ReactNode;
  /**
   * La fila de botones. NO la pone el armazón: sus etiquetas, su orden y
   * cuál es la acción principal son de cada formulario. Ver
   * `ProductFormActions`.
   */
  actions?: ReactNode;
  className?: string;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El ARMAZÓN de un formulario por pasos: la card, el progreso, la región
 * del paso y el foco.
 *
 * ── Por qué existe ──────────────────────────────────────────────────
 * `Stepper`, `WizardNav` y `useWizard` eran tres piezas sueltas y el
 * armazón que las junta estaba escrito a mano dentro de la pantalla del
 * producto. En cuanto una segunda pantalla se recorre por pasos —los
 * detalles editoriales— copiarlo significaría tener dos armazones que se
 * separan poco a poco: uno arregla el foco, el otro no; uno anuncia la
 * región, el otro se queda mudo. Y lo que se pierde por ahí no se ve
 * hasta que alguien navega con el teclado.
 *
 * ── Lo que se lleva dentro ──────────────────────────────────────────
 *  · El progreso (`Stepper`) con los rótulos traducidos.
 *  · La región del paso: `role="group"` con el NOMBRE del paso, que es lo
 *    que oye quien no ve la pantalla cambiar entera.
 *  · El foco al cambiar de paso, con su excepción: si el paso trae un
 *    error del servidor, el foco va A ESE CAMPO.
 *  · Que en el PRIMER render no se robe el foco.
 *  · El salto al primer paso con error cuando un guardado se cae por la
 *    validación del navegador — que es lo que permite guardar desde
 *    cualquier paso sin que el botón parezca roto.
 *
 * ── Lo que NO se lleva ──────────────────────────────────────────────
 * La botonera. "Siguiente" negro pegado a "Crear producto", "Cancelar"
 * separado de los dos: ese orden es una decisión del formulario del
 * producto, y otro formulario tendrá la suya. El armazón deja el hueco.
 *
 * Tampoco el `<form>`: quien envía es la pantalla, y el armazón vive
 * dentro de él.
 * ═══════════════════════════════════════════════════════════════════
 */
export function FormWizard({
  steps,
  wizard,
  progressLabel,
  focusStepError,
  submitCount = 0,
  firstStepWithError,
  children,
  actions,
  className,
}: FormWizardProps) {
  const { t } = useTranslation();
  const regionRef = useRef<HTMLDivElement>(null);
  const current = steps[wizard.step];

  useStepFocus(wizard.step, regionRef, focusStepError);
  useSubmitJump(submitCount, wizard, firstStepWithError);

  return (
    <GlassPanel className={cn("grid gap-3 p-5 sm:p-6", className)}>
      {/*
        Los rótulos se declaran en español donde se declara el reparto de
        pasos, y se traducen aquí, al pintarlos, como cualquier otra
        cadena de la interfaz.
      */}
      <Stepper
        steps={steps.map((step) => t(step.label))}
        current={wizard.step}
        label={t(progressLabel)}
      />

      {/*
        La región del paso. `outline-none` porque recibe el foco al
        cambiar de paso y un contorno alrededor de medio formulario no
        dice nada; lo que informa es que el lector de pantalla anuncie el
        grupo con su nombre.
      */}
      <div
        ref={regionRef}
        tabIndex={-1}
        role="group"
        aria-label={current ? t(current.label) : undefined}
        className="grid gap-3 outline-none"
      >
        {current?.description && (
          <p className="text-[13px] leading-relaxed text-ds-ink-3">
            {t(current.description)}
          </p>
        )}

        {children}
      </div>

      {actions}
    </GlassPanel>
  );
}

/**
 * Enter avanza de paso; no envía a medias.
 *
 * El botón de guardar está siempre en pantalla, así que Enter en un campo
 * cualquiera enviaría el formulario entero desde el paso 2. Con esto,
 * Enter significa siempre lo mismo: seguir.
 *
 * Los textos largos, los botones y los enlaces quedan fuera: en un
 * `<textarea>` Enter es un salto de línea, y en un botón es "actívame".
 * Robárselo rompería las listas editoriales y la botonera.
 *
 * Se devuelve un manejador en vez de vivir dentro de `FormWizard` porque
 * el `<form>` es de la pantalla: el armazón está DENTRO de él y desde
 * dentro no se pueden atender las teclas de todo el formulario.
 */
export function useWizardEnter(wizard: Wizard) {
  return (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter" || wizard.isLast) return;

    const target = event.target as HTMLElement;
    if (
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement
    ) {
      return;
    }

    event.preventDefault();
    void wizard.next();
  };
}

/**
 * A dónde va el foco al cambiar de paso.
 *
 * Sin esto, el foco se queda en el botón "Siguiente" —que acaba de
 * cambiar de significado bajo el cursor— y quien usa lector de pantalla
 * no se entera de que la pantalla entera se sustituyó.
 *
 * Si el paso al que se llega trae un error (el "ese slug ya existe" que
 * devolvió el servidor estando en el último paso, por ejemplo), el foco
 * va A ESE CAMPO: es lo que hay que corregir y es donde está el mensaje.
 * Si no, a la región del paso, que se anuncia con su nombre.
 *
 * En el primer render no se toca nada: ahí manda el orden natural del
 * documento, y robarlo dejaría el foco en un contenedor en vez de al
 * principio de la página.
 */
function useStepFocus(
  step: number,
  region: RefObject<HTMLDivElement | null>,
  focusStepError?: (step: number) => boolean,
) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (focusStepError?.(step)) return;
    region.current?.focus();
  }, [focusStepError, region, step]);
}

/**
 * Si un intento de guardar se cae por la validación del NAVEGADOR, lleva
 * al primer paso que tenga un error.
 *
 * Sin esto, pulsar "Guardar" en el paso 1 sin haber puesto precio no
 * haría absolutamente nada en pantalla: el error existe, pero está en un
 * paso que no se está viendo. Es lo mismo que se hace con los errores que
 * señala el servidor, solo que este ocurre sin salir del navegador y por
 * eso hace falta la otra mitad.
 *
 * Se dispara con `submitCount` —que sube en CADA intento, salga bien o
 * mal— y no con los errores: los errores cambian también mientras se
 * escribe, y saltar de paso a media palabra sería inaceptable.
 */
function useSubmitJump(
  submitCount: number,
  wizard: Wizard,
  firstStepWithError?: () => number,
) {
  const { goTo } = wizard;

  useEffect(() => {
    if (submitCount === 0 || !firstStepWithError) return;
    const step = firstStepWithError();
    if (step >= 0) goTo(step);
  }, [firstStepWithError, goTo, submitCount]);
}
